import { Badge, Box, Inline, Text } from "@sembark-travel/ui-base"
import { IAccount } from "./store"
import { useCallback } from "react"
import {
	AsyncSearchList,
	DuplicateEntityField,
	MergeConfirmation,
	SelectPrimaryField,
	MergeEntities,
	useMergeEntities,
} from "../Hotels"

export function MergeAccounts({
	onSuccess,
	onCancel,
}: {
	onSuccess: (primary: IAccount) => void
	onCancel: () => void
}) {
	const mergeHook = useMergeEntities<IAccount>({
		mergeUrl: "accounting/accounts/merge",
		getId: (a) => a.id,
		onSuccess,
	})
	const getId = useCallback((a: IAccount) => a.id, [])

	return (
		<MergeEntities<IAccount>
			title="Merge Duplicate Accounts"
			entityLabel="Account"
			primaryField="primary"
			duplicatesField="duplicates"
			useMergeHook={mergeHook}
			onSubmit={async (values, confirm) => {
				return await mergeHook.merge(
					values.primary,
					values.duplicates,
					confirm,
					"Account"
				)
			}}
			onCancel={onCancel}
			renderDuplicates={() => <AccountDuplicatesField />}
			renderPrimary={() => (
				<SelectPrimaryField
					getId={getId}
					duplicatesField="duplicates"
					renderItem={(account) => <AccountItem account={account} />}
					primaryField="primary"
					entityLabel="Account"
				/>
			)}
			renderConfirmation={() => (
				<MergeConfirmation
					entityLabel="Account"
					primaryField="primary"
					getId={getId}
					duplicatesField="duplicates"
					renderItem={(account) => <AccountItem account={account} />}
					consequences={
						"All transactions and balances will be consolidated into the primary account. -- The remaining duplicate accounts will be disabled"
					}
				/>
			)}
		/>
	)
}
type AccountItemProps = {
	account: IAccount
}

function AccountItem({ account }: AccountItemProps) {
	const { name, entity_type } = account

	return (
		<Inline flexGrow={"1"} justifyContent="between" alignItems="start" gap="3">
			<Inline gap="2" alignItems="center">
				<Text fontWeight="semibold">{name}</Text>
			</Inline>
			<Box>
				<Badge>{entity_type}</Badge>
			</Box>
		</Inline>
	)
}
function AccountDuplicatesField() {
	return (
		<DuplicateEntityField<IAccount>
			title="Duplicate Accounts"
			description="Please select the duplicate accounts."
			searchName="q"
			duplicatesName="duplicates"
			SearchListComponent={AsyncSearchList}
			url="accounting/accounts"
			getId={(a) => a.id}
			renderItem={(a) => <AccountItem account={a} />}
			selectedRenderItems={(a) => a.name}
			params={{ manual_only: true }}
		/>
	)
}
